import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'react-signature-pad-wrapper';
import { keyMap } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import { SignatureDataType } from 'global.interfaces';
import './style.scss';

const PREFIX = 'data:image/png;base64,';

export type SignatureProps = {
  className?: string;
  data?: SignatureDataType | null;
  initialValue?: SignatureDataType | null;
  disabled?: boolean;
  onChange: (state: SignatureDataType | null) => void;
};

export type SignatureRef = { setData: (image: SignatureDataType) => void; validate: () => void };

const Signature = forwardRef((props: SignatureProps, ref) => {
  const { disabled, className, onChange, initialValue, data } = props;

  const { i18n } = useTranslation();
  const [isValid, setIsValid] = useState(true);
  const signature = useRef<SignaturePad>(null);

  useImperativeHandle(ref, () => ({ setData, validate }));

  useEffect(() => {
    if (initialValue) {
      setData(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      setData(data);
    }
  }, [data]);

  const changeHandler = useCallback(() => {
    if (!signature || !signature.current) {
      return;
    }
    const base64Signature = signature.current.toDataURL('image/png');
    const data = base64Signature.slice(PREFIX.length);

    signature.current.canvas.current?.toBlob((blob) => {
      onChange({ metaInfo: PREFIX, data, signatureFile: blob });
    });
  }, [onChange]);

  useEffect(() => {
    //@ts-ignore
    if (signature.current && signature?.current?.signaturePad) {
      const { current } = signature;

      //@ts-ignore
      current.signaturePad.addEventListener('endStroke', changeHandler);
      // clean up the event handler
      return function cleanup() {
        //@ts-ignore
        current.signaturePad.removeEventListener('endStroke', changeHandler);
      };
    }
  }, [changeHandler]);

  const validate = () => {
    if (!signature || !signature.current) {
      return true;
    }

    const isValid = !signature.current.isEmpty();

    setIsValid(isValid);

    return isValid;
  };

  const clearHandler = () => {
    if (!signature || !signature.current) {
      return;
    }
    signature.current.clear();
    onChange(null);
  };

  const setData = (image: SignatureDataType) => {
    if (!signature || !signature.current) {
      return;
    }
    if (image && image.metaInfo && image.data) {
      signature.current.fromDataURL(image.metaInfo + image.data);
    }
  };

  return (
    <div className={cn('signature-wrapper', className)}>
      <div className={cn('signature', { invalid: !isValid, disabled })}>
        <SignaturePad
          redrawOnResize
          ref={signature}
          options={{
            minWidth: 1,
            maxWidth: 2,
            penColor: 'black',
          }}
        />
      </div>
      <div className="signature-controls">
        {!disabled && (
          <button className="form-button" type="button" onClick={clearHandler}>
            {i18n.t(keyMap.signatureClearBtn)}
          </button>
        )}
      </div>
    </div>
  );
});

export default Signature;
